import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import { ProgressBar, Button } from '../common';
import MCQQuestion from './MCQQuestion';

/**
 * MCQ Section Component
 * Handles nutrition or cognitive assessment flow
 */
const MCQSection = ({ 
  sectionType, // 'nutrition' or 'cognitive'
  questions,
  onComplete 
}) => {
  const navigate = useNavigate();
  const { 
    saveNutritionResponse, 
    saveCognitiveResponse, 
    nextStep 
  } = useAssessment();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [isCompleting, setIsCompleting] = useState(false);

  // Reset state when section type changes
  useEffect(() => {
    setCurrentQuestionIndex(0);
    setResponses([]);
    setIsCompleting(false);
  }, [sectionType]);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex) / questions.length) * 100;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const sectionInfo = {
    nutrition: {
      title: 'Nutrition Assessment',
      subtitle: 'Help us understand your dietary habits',
      icon: '🥗',
      color: 'green'
    },
    cognitive: {
      title: 'Cognitive Assessment',
      subtitle: 'Discover your learning style and personality traits',
      icon: '🧠',
      color: 'purple'
    }
  };

  const info = sectionInfo[sectionType];

  const handleAnswer = async (answerData) => {
    // Save response
    const newResponses = [...responses, answerData];
    setResponses(newResponses);

    // Save to context/storage
    if (sectionType === 'nutrition') {
      await saveNutritionResponse(
        answerData.questionId,
        answerData.answer,
        answerData.score
      );
    } else {
      await saveCognitiveResponse(
        answerData.questionId,
        answerData.answer,
        answerData.score
      );
    }

    // Move to next question or complete
    if (isLastQuestion) {
      setIsCompleting(true);
      setTimeout(async () => {
        try {
          if (onComplete) {
            await onComplete(newResponses);
          }
          await nextStep();
          console.log(`${sectionType} assessment completed, moving to next step`);
        } catch (error) {
          console.error('Error completing section:', error);
        }
      }, 1500);
    } else {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 100);
    }
  };

  if (isCompleting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center animate-fade-in">
          <div className="text-8xl mb-6 animate-bounce">
            {info.icon}
          </div>
          <h2 className="text-3xl font-bold text-slate-100 mb-4">
            {info.title} Complete!
          </h2>
          <p className="text-slate-400 mb-6">
            Processing your responses...
          </p>
          <div className="flex justify-center">
            <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-6xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">{info.icon}</div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2">
            {info.title}
          </h1>
          <p className="text-slate-400 mb-6">
            {info.subtitle}
          </p>

          {/* Progress Bar */}
          <div className="max-w-2xl mx-auto">
            <ProgressBar
              current={currentQuestionIndex}
              total={questions.length}
              showLabel={true}
              showPercentage={true}
              size="lg"
              color={info.color === 'green' ? 'secondary' : 'primary'}
            />
          </div>
        </div>

        {/* Question */}
        <MCQQuestion
          question={currentQuestion}
          onAnswer={handleAnswer}
          autoAdvanceDelay={800}
        />

        {/* Question Counter */}
        <div className="text-center mt-6 text-slate-500 text-sm">
          Question {currentQuestionIndex + 1} of {questions.length}
        </div>

        {/* Navigation Hint */}
        {currentQuestionIndex === 0 && (
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4 text-center animate-pulse">
              <p className="text-primary-300 text-sm">
                💡 <strong>Tip:</strong> Simply click your answer - no need to click "Next"! 
                The assessment will automatically move forward.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MCQSection;
